// src/pages/Dashboard/components/MeetingRecap.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. Tyler: maybe after the meeting, a recap.
//
// WHAT THIS MORNING'S MEETING WAS ABOUT, FOR WHOEVER WAS NOT LISTENING.
//
// Shown on a salesperson's Today once their manager has marked the meeting
// held, and only then: an agenda that was prepared but not run is a draft, and
// telling the floor "this morning we covered" about a meeting that did not
// happen is fake data with a friendly face.
//
// TWO THINGS AND NO MORE. The one skill focus, because naming ONE is the point
// of his meeting and this is how it survives past nine o'clock. And who was
// called out, because recognition a boss witnesses is worth more than a badge,
// and whoever was on a phone during the meeting deserves to see it too.
//
// The numbers and the aged opportunities stay off it. Results, appointments
// and deliveries are the manager's to say out loud, and a salesperson's Today
// is not the place to reprint the store's month.

import { Box, HStack, VStack, Text, Button, Wrap, WrapItem } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Sun, Dumbbell } from 'lucide-react'
import Medallion from '../../../components/ui/Medallion.jsx'
import { markByKey } from '../../../content/marks'
import { getStepByNumber } from '../../../content/steps'
import { firstNameFor } from '../../../lib/names.js'
import { repLink } from '../../../lib/morningRoutine.js'

export default function MeetingRecap({ meeting, calledOut, people, userId }) {
  if (!meeting?.held_at) return null
  const focus = String(meeting.skill_focus || '').trim()
  const step = meeting.step_number ? getStepByNumber(meeting.step_number) : null
  if (!focus && !step && (!calledOut || calledOut.length === 0)) return null

  const at = new Date(meeting.held_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="card" px={{ base: 5, md: 6 }} py={5}>
      <HStack spacing={2} mb={3} color="inkDim">
        <Sun size={14} aria-hidden="true" />
        <Text textStyle="eyebrow">THIS MORNING'S MEETING, {at.toUpperCase()}</Text>
      </HStack>

      <VStack align="stretch" spacing={4}>
        {(focus || step) && (
          <Box>
            <Text fontSize="sm" color="inkMuted" mb={0.5}>
              Today's one focus
            </Text>
            <Text fontSize="lg" fontWeight={600} letterSpacing="-0.01em" lineHeight={1.35}>
              {focus || `Step ${step.number}, ${step.title}`}
            </Text>
            {step && focus && (
              <Text fontSize="sm" color="inkDim" fontFamily="mono" mt={0.5}>
                Step {step.number}, {step.title}
              </Text>
            )}
            {step && (
              <Button
                as={RouterLink}
                to={repLink(step.number)}
                size="sm"
                variant="outline"
                mt={3}
                leftIcon={<Dumbbell size={14} />}
              >
                Sixty seconds on it with the coach
              </Button>
            )}
          </Box>
        )}

        {calledOut && calledOut.length > 0 && (
          <Box>
            <Text fontSize="sm" color="inkMuted" mb={2}>
              Called out
            </Text>
            <Wrap spacing={3}>
              {calledOut.map((r) => {
                const m = markByKey(r.mark)
                const who = r.given_to === userId ? 'You' : firstNameFor(people[r.given_to]) || 'Somebody'
                return (
                  <WrapItem key={r.id}>
                    <HStack spacing={2} pr={3} py={1} pl={1} borderRadius="pill" border="1px solid" borderColor="line">
                      <Medallion mark={r.mark} size={26} />
                      <Text fontSize="sm">
                        <Text as="span" fontWeight={600}>{who}</Text>
                        <Text as="span" color="inkMuted">{' · '}{m?.name || 'noticed'}</Text>
                      </Text>
                    </HStack>
                  </WrapItem>
                )
              })}
            </Wrap>
          </Box>
        )}
      </VStack>
    </Box>
  )
}
